import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Colors } from '_styles';
import { MdConnectingAirports } from "react-icons/md";
import ConnectionFlightInformation from './connection-flight-information';

/**
 * ConnectionBadge
 *
 * Self-contained badge + hover-over detail modal for connection passengers.
 *
 * @param {object}  props.onwards    - onwards flight object (carrierCode, flightNumber, origin, destination, departureDate)
 * @param {object}  props.inbound    - inbound flight object (carrierCode, flightNumber, origin) — used when onwards is absent
 * @param {"icon"|"badge"} props.variant - "icon" renders just the icon (for job cards), "badge" renders a labelled badge (for passenger rows)
 * @param {number}  props.iconSize   - icon size, defaults to 16
 */
const ConnectionBadge = ({ onwards, inbound, variant = 'badge', iconSize = 16 }) => {
    const [tooltip, setTooltip] = useState(null);
    const timer = useRef(null);

    const connectionData = onwards || inbound;
    if (!connectionData) return null;

    const isOnwards = !!onwards;
    const flightInfoProps = isOnwards ? onwards : inbound;

    const handleMouseEnter = (e) => {
        clearTimeout(timer.current);
        setTooltip({ x: e.clientX, y: e.clientY });
    };

    const handleMouseLeave = () => {
        timer.current = setTimeout(() => setTooltip(null), 150);
    };

    const handleMouseMove = (e) => {
        if (tooltip) setTooltip({ x: e.clientX, y: e.clientY });
    };

    return (
        <>
            <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    cursor: 'default',
                    ...(variant === 'badge' ? {
                        background: '#E8F4FD',
                        border: `1px solid ${Colors.PRM_LIGHT_BLUE}`,
                        borderRadius: 4,
                        padding: '2px 6px',
                    } : {}),
                }}
            >
                <MdConnectingAirports size={iconSize} color={Colors.PRM_LIGHT_BLUE} />
                {variant === 'badge' && (
                    <span style={{ fontSize: 11, fontWeight: 600, color: Colors.PRM_LIGHT_BLUE, whiteSpace: 'nowrap' }}>
                        Connection
                    </span>
                )}
            </div>

            {tooltip && createPortal(
                <div
                    onMouseEnter={() => clearTimeout(timer.current)}
                    onMouseLeave={() => setTooltip(null)}
                    style={{
                        position: 'fixed',
                        top: tooltip.y,
                        left: tooltip.x + 8,
                        transform: 'translateY(-50%)',
                        background: 'white',
                        border: '1px solid lightgray',
                        borderRadius: 8,
                        padding: '10px 14px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 999999999,
                        minWidth: 180,
                        pointerEvents: 'auto',
                    }}
                >
                    <div style={{ fontSize: 12, fontWeight: 'bold', color: '#333', marginBottom: 4, borderBottom: '1px solid #eee', paddingBottom: 6 }}>
                        {isOnwards ? 'Connecting Flight' : 'Inbound Flight'}
                    </div>
                    <ConnectionFlightInformation onwards={flightInfoProps} />
                </div>,
                document.body
            )}
        </>
    );
};

export default React.memo(ConnectionBadge);
